import type { TranslateFunction } from "../types/index.js";
import { parseDate } from "./date.js";
import { DEFAULT_LOCALE, resolveLocale } from "./date-locale.js";

const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
const PADDED_TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
const MONTH_YEAR_OPTIONS: Intl.DateTimeFormatOptions = { month: "long", year: "numeric" };
const FULL_DATE_OPTIONS: Intl.DateTimeFormatOptions = { weekday: "long", month: "long", day: "numeric", year: "numeric" };
const DAY_MONTH_OPTIONS: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
const NUMERIC_DATE_OPTIONS: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric" };
const COMPACT_DATE_OPTIONS: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "2-digit" };

const DateStyle = {
	TIME: "time",
	PADDED_TIME: "paddedTime",
	SHORT_DATE: "shortDate",
	MONTH_YEAR: "monthYear",
	FULL_DATE: "fullDate",
	DAY_MONTH: "dayMonth",
	NUMERIC_DATE: "numericDate",
	COMPACT_DATE: "compactDate",
} as const;
type DateStyle = (typeof DateStyle)[keyof typeof DateStyle];

const OPTIONS_BY_STYLE: Record<DateStyle, Intl.DateTimeFormatOptions> = {
	[DateStyle.TIME]: TIME_OPTIONS,
	[DateStyle.PADDED_TIME]: PADDED_TIME_OPTIONS,
	[DateStyle.SHORT_DATE]: SHORT_DATE_OPTIONS,
	[DateStyle.MONTH_YEAR]: MONTH_YEAR_OPTIONS,
	[DateStyle.FULL_DATE]: FULL_DATE_OPTIONS,
	[DateStyle.DAY_MONTH]: DAY_MONTH_OPTIONS,
	[DateStyle.NUMERIC_DATE]: NUMERIC_DATE_OPTIONS,
	[DateStyle.COMPACT_DATE]: COMPACT_DATE_OPTIONS,
};

const TODAY_KEY = "common:dates.today";
const TOMORROW_KEY = "common:dates.tomorrow";
const RANGE_SEPARATOR = "·";
const TIME_SEPARATOR = "–";

const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

function getDateTimeFormatter(locale: string, style: DateStyle): Intl.DateTimeFormat {
	const cacheKey = `${locale}:${style}`;
	const cached = dateTimeFormatters.get(cacheKey);
	if (cached) return cached;
	const options = OPTIONS_BY_STYLE[style];
	let formatter: Intl.DateTimeFormat;
	try {
		formatter = new Intl.DateTimeFormat(locale, options);
	} catch {
		formatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, options);
	}
	dateTimeFormatters.set(cacheKey, formatter);
	return formatter;
}

function format(date: Date | string | null | undefined, language: string, style: DateStyle): string {
	const parsed = parseDate(date);
	if (!parsed) return "";
	return getDateTimeFormatter(resolveLocale(language), style).format(parsed);
}

export function formatTime(date: Date | string | null | undefined, language: string): string {
	return format(date, language, DateStyle.TIME);
}

export function formatDate(date: Date | string | null | undefined, language: string): string {
	return format(date, language, DateStyle.SHORT_DATE);
}

export function formatMonthYear(date: Date | string | null | undefined, language: string): string {
	return format(date, language, DateStyle.MONTH_YEAR);
}

export function formatFullDate(date: Date | string | null | undefined, language: string): string {
	return format(date, language, DateStyle.FULL_DATE);
}

export function formatDayMonth(date: Date | string | null | undefined, language: string): string {
	return format(date, language, DateStyle.DAY_MONTH);
}

export function formatDateTime(date: Date | string | null | undefined, language: string): string {
	const parsed = parseDate(date);
	if (!parsed) return "";
	return `${formatDate(parsed, language)} ${RANGE_SEPARATOR} ${formatTime(parsed, language)}`;
}

export function formatDateTimeRange(startAt: Date | string, endAt: Date | string, language: string): string {
	const start = parseDate(startAt);
	const end = parseDate(endAt);
	if (!start || !end) return "";
	const locale = resolveLocale(language);
	const timeFormatter = getDateTimeFormatter(locale, DateStyle.PADDED_TIME);
	return `${formatDate(start, language)} ${RANGE_SEPARATOR} ${timeFormatter.format(start)} ${TIME_SEPARATOR} ${timeFormatter.format(end)}`;
}

export function formatClockTime(time24: string, language: string): string {
	const [hourPart, minutePart] = time24.split(":");
	if (!hourPart || !minutePart) return time24;
	const hour = Number(hourPart);
	const minute = Number(minutePart);
	if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) return time24;
	const reference = new Date(2000, 0, 1, hour, minute);
	return getDateTimeFormatter(resolveLocale(language), DateStyle.TIME).format(reference);
}

export interface RelativeDateRefs {
	today: number;
	tomorrow: number;
}

export function createRelativeDateRefs(): RelativeDateRefs {
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const tomorrow = new Date(today);
	tomorrow.setDate(tomorrow.getDate() + 1);
	return { today: today.getTime(), tomorrow: tomorrow.getTime() };
}

export function getRelativeDateLabel(
	date: Date | string,
	t: TranslateFunction,
	language: string,
	refs: RelativeDateRefs = createRelativeDateRefs(),
): string {
	const parsed = parseDate(date);
	if (!parsed) return "";
	const target = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
	if (target === refs.today) return t(TODAY_KEY);
	if (target === refs.tomorrow) return t(TOMORROW_KEY);
	return formatDate(parsed, language);
}

export function isOngoing(startAt: Date | string, endAt: Date | string): boolean {
	const start = parseDate(startAt);
	const end = parseDate(endAt);
	if (!start || !end) return false;
	const now = Date.now();
	return start.getTime() <= now && now < end.getTime();
}

export function formatDisplayDate(date: Date | string | null | undefined, language?: string): string {
	return format(date, language ?? DEFAULT_LOCALE, DateStyle.NUMERIC_DATE);
}

export function formatRelativeTimestamp(date: Date | string | null | undefined, language?: string): string {
	const parsed = parseDate(date);
	if (!parsed) return "";
	const locale = resolveLocale(language);
	if (parsed.toDateString() === new Date().toDateString()) {
		return getDateTimeFormatter(locale, DateStyle.TIME).format(parsed);
	}
	return getDateTimeFormatter(locale, DateStyle.COMPACT_DATE).format(parsed);
}
